/**
 * ArtifactCanvas — the right pane on the fullpage /assistant route. Shows
 * whichever artifact is pinned (or an empty state when nothing is pinned).
 *
 * Composition:
 *   CanvasHeader (tabs + actions)
 *   WorkflowProgress (stage breadcrumb, sales-selection only)
 *   ApplicationProfileCard (variant="canvas") — PERSISTENT control surface,
 *     visible for the whole selection flow (chatStore.latestApplicationProfile);
 *     see below.
 *   TableArtifact / PlotArtifact / ReportArtifact / KpiGroup / CanvasEmptyState
 *
 * Tab selection persists in chatStore.canvasTab. When a tool-output auto-
 * pins, chatStore switches the tab to match the artifact type, so users
 * see the right view without clicking. Only canvas-renderable artifacts are
 * pinned (chatStore.NON_CANVAS_ARTIFACT_TYPES excludes ask_user / nav_link /
 * data_series, which render inline in the chat instead).
 *
 * The application-profile card used to repeat inline in every chat message
 * (ChatMessage's variant="chat" render) and get overwritten in the canvas by
 * the moment a ranking pinned. It now lives ONCE, persistently, above the
 * Table/Plot/Report tabs — driven by `latestApplicationProfile`, which
 * (unlike `pinnedArtifact`) survives every later `ranked_cells_table` re-rank
 * pin, so the constraints + weight sliders stay visible and live-editable for
 * the whole flow.
 *
 * The Report tab renders a read-only sales one-pager (ReportArtifact);
 * server-side PDF export is the open SS-7 backend item.
 */

import { useEffect, useRef, useState } from 'preact/hooks'

import {
  pinnedArtifact,
  canvasTab,
  chatStreaming,
  unpinArtifact,
  latestApplicationProfile,
  reRankPending,
  selectionContext,
} from '../../../state/chatStore.js'
import { KpiGroup } from '../KpiGroup.jsx'
import { CanvasEmptyState } from './CanvasEmptyState.jsx'
import { CanvasHandoffCTAs } from './CanvasHandoffCTAs.jsx'
import { ValidationNextSteps } from './ValidationNextSteps.jsx'
import { CanvasHeader } from './CanvasHeader.jsx'
import { ApplicationProfileCard } from './ApplicationProfileCard.jsx'
import { PlotArtifact } from './PlotArtifact.jsx'
import { SalesRagonePlot } from './SalesRagonePlot.jsx'
import { RankedCellsTable } from './RankedCellsTable.jsx'
import { ReportArtifact } from './ReportArtifact.jsx'
import { TableArtifact } from './TableArtifact.jsx'
import { WorkflowProgress } from './WorkflowProgress.jsx'

// localStorage key for the (wide-mode) rail collapse preference.
const RAIL_COLLAPSED_KEY = 'ion.canvas.railCollapsed'
// Container-query breakpoint (must match the @container rule in ion.css).
// Above this the controls move into a left rail; below they stack.
const RAIL_WIDE_MIN = 760

/**
 * Rail collapse state. `collapsedPref` is the user's explicit choice, persisted
 * in localStorage; when unset we fall back to a sensible default: the rail is
 * open on a wide canvas, and auto-collapses on a narrow one once a shortlist
 * exists (so the compact strip doesn't eat the plot). Width is measured with a
 * ResizeObserver on the layout element — the split is a container query, so a
 * viewport media query would read the wrong width (the canvas is ~60% of it).
 */
function useRailCollapse(reRankReady) {
  const layoutRef = useRef(null)
  const [collapsedPref, setCollapsedPref] = useState(() => {
    try {
      const v = localStorage.getItem(RAIL_COLLAPSED_KEY)
      return v === null ? null : v === 'true'
    } catch {
      return null
    }
  })
  const [isWide, setIsWide] = useState(true)

  useEffect(() => {
    const el = layoutRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? el.clientWidth
      setIsWide(w >= RAIL_WIDE_MIN)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const autoCollapsed = !isWide && reRankReady
  const collapsed = collapsedPref === null ? autoCollapsed : collapsedPref

  const toggle = () => {
    const next = !collapsed
    setCollapsedPref(next)
    try {
      localStorage.setItem(RAIL_COLLAPSED_KEY, String(next))
    } catch {
      /* private mode / storage disabled — collapse still works in-session */
    }
  }

  return { layoutRef, collapsed, toggle }
}

export function ArtifactCanvas() {
  const artifact = pinnedArtifact.value
  const tab = canvasTab.value
  const profile = latestApplicationProfile.value
  // A re-rank is "live" for the updating cue when either a chat turn is
  // streaming (the LLM path) or a slider drag fired a deterministic re-rank
  // (WeightSlidersSection sets this while its request is in flight).
  const isUpdating = chatStreaming.value || reRankPending.value
  // Mirrors the constraint/weight edit gate — used only to decide the narrow
  // default (auto-collapse the strip once there's a shortlist to look at).
  const reRankReady = (selectionContext.value?.candidateSet || []).length >= 2
  const { layoutRef, collapsed, toggle } = useRailCollapse(reRankReady)

  return (
    <section class="ion-canvas" aria-label="Batemo Ion artifact canvas">
      <CanvasHeader artifact={artifact} />

      <WorkflowProgress />

      {/* Container-query layout: a left controls-rail + right live stage on a
          wide canvas, stacked (rail as a compact strip on top) when narrow.
          `.ion-canvas__layout` owns the container context; the split below it
          is what the @container rule reshapes. */}
      <div class="ion-canvas__layout" ref={layoutRef}>
        <div
          class={`ion-canvas__split${collapsed ? ' ion-canvas__split--collapsed' : ''}`}
        >
          {profile && (
            <aside
              class="ion-canvas__rail"
              aria-label="Selection controls"
            >
              <div class="ion-canvas__rail-head">
                <span class="ion-canvas__rail-title">Requirements</span>
                <button
                  type="button"
                  class="ion-canvas__rail-toggle"
                  onClick={toggle}
                  aria-expanded={!collapsed}
                  aria-label={
                    collapsed ? 'Show requirement controls' : 'Hide requirement controls'
                  }
                  title={collapsed ? 'Show requirements' : 'Hide requirements'}
                >
                  <RailToggleIcon collapsed={collapsed} />
                </button>
              </div>
              {!collapsed && (
                <div class="ion-canvas__rail-body ion-canvas__profile-panel">
                  <ApplicationProfileCard data={profile} variant="canvas" />
                </div>
              )}
            </aside>
          )}

          <div class="ion-canvas__stage">
            <div class="ion-canvas__body">
              {!artifact ? (
                <CanvasEmptyState />
              ) : (
                // Keyed by TAB only — a live re-rank (new artifact id, same tab)
                // must NOT remount the plot (that would kill the in-place
                // narrowing). Tab switches remount → crossfade.
                <div class="ion-canvas__pane" key={tab}>
                  {renderArtifact(artifact, tab)}
                </div>
              )}
              {/* Subtle "Updating…" cue while a re-rank refreshes the live
                  shortlist — the chart/table stay mounted (no re-init flash).
                  Anchored inside the stage, so in the rail layout the shimmer
                  sits over the plot, never over the controls rail. */}
              {artifact && artifact.type === 'ranked_cells_table' && isUpdating && (
                <div
                  class={`ion-canvas__updating${reRankPending.value ? ' ion-canvas__updating--live' : ''}`}
                  aria-live="polite"
                >
                  <span class="ion-working__dot" aria-hidden="true" />
                  <span>
                    {reRankPending.value
                      ? 'Re-ranking with your new requirements…'
                      : tab === 'plot'
                        ? 'Updating plot with your requirements…'
                        : 'Updating…'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <CanvasHandoffCTAs />
      <ValidationNextSteps />
    </section>
  )
}

/** Rail collapse/expand glyph — a gear when collapsed (open the controls), a
 *  left-chevron when expanded (tuck the rail away). Pure inline SVG so it
 *  inherits currentColor and needs no icon-library import. */
function RailToggleIcon({ collapsed }) {
  if (collapsed) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.6" />
        <path
          d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.2 5.2l2.1 2.1M16.7 16.7l2.1 2.1M18.8 5.2l-2.1 2.1M7.3 16.7l-2.1 2.1"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>
    )
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M14 6l-6 6 6 6"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  )
}

function renderArtifact(artifact, tab) {
  // The active tab can override the pinned artifact's natural variant —
  // e.g. a tool returned both a table and a figure, the user clicked Plot.
  // In F1 we only have one payload per pin, so tab merely picks how to
  // render that payload. Future: separate slots per tab.

  // Sales-Selection profile: the constraints/weights now live in the PERSISTENT
  // profile panel above this body (see ArtifactCanvas — latestApplicationProfile),
  // not in this swapped pane. Here we show the live full-catalogue Ragone from
  // the very first question — all cells on fixed axes, greying out as the user
  // answers — so the plot never flips back to a card mid re-rank. (The Report
  // tab still renders the report.)
  if (artifact.type === 'application_profile') {
    if (tab === 'report') return <ReportArtifact artifact={artifact} />
    return <SalesRagonePlot plotData={artifact.data?.plot_data || null} />
  }

  // Sales-Selection ranked shortlist — Top-N table with score bars,
  // constraint-pass icons, and click-through to cell detail. The same
  // backend payload also carries plot_data for the Plot tab (Ragone), so
  // only route to the table renderer when the user is on the default tab.
  if (artifact.type === 'ranked_cells_table' && tab !== 'plot' && tab !== 'report') {
    return <RankedCellsTable data={artifact.data} />
  }

  // Sales-Selection ranked shortlist on the Plot tab → render the SAME
  // <SalesRagonePlot> element (same component + position) as the
  // application_profile branch above, NOT PlotArtifact. A chat-answer turn
  // re-pins application_profile (apply) then ranked_cells_table (rank); if the
  // two rendered different component wrappers under this one pane, Preact
  // unmounted+remounted the chart every turn — the "chart disappears then
  // reappears" flash. Rendering SalesRagonePlot directly for both keeps ONE
  // instance mounted, so the setOption(replaceMerge) morph animates the whole
  // turn instead of reloading. Both artifact types carry plot_data (same shape).
  if (artifact.type === 'ranked_cells_table' && tab === 'plot') {
    return (
      <SalesRagonePlot
        plotData={artifact.data?.plot_data || null}
        caption={artifact.data?.plot_data?.caption}
      />
    )
  }

  if (artifact.type === 'kpi-group') {
    return <KpiGroup group={artifact.data} />
  }

  // Route to PlotArtifact for any plot-shaped artifact: legacy 'figure'
  // (server-rendered URL), new 'plotly_chart' (interactive Plotly config),
  // or 'data_value' (verified-data chip from get_cell_metric). Without
  // each type in this match, the artifact would fall through to
  // TableArtifact and render as JSON garbage.
  if (
    tab === 'plot' ||
    artifact.type === 'figure' ||
    artifact.type === 'plotly_chart' ||
    artifact.type === 'data_value'
  ) {
    return <PlotArtifact data={artifact.data} />
  }

  if (tab === 'report' || artifact.type === 'report') {
    return <ReportArtifact artifact={artifact} />
  }

  // Default / 'table' tab
  return <TableArtifact data={artifact.data} />
}

// Re-export unpin so a consumer (e.g. canvas keyboard shortcut) can drop it.
export { unpinArtifact }
